// @ts-nocheck
import { formatCurrency, formatDate } from '../lib/utils';

interface ImportPreviewProps {
  preview: {
    rows: Array<{
      date: string;
      type: string;
      reference_number: string | null;
      client_name: string;
      amount: number;
      cert_status: string;
    }>;
    clientMatches: Array<{
      importName: string;
      matchedClient: { id: number; name: string } | null;
      matchType: string;
      normalizedName: string;
    }>;
    duplicates: Array<{
      rowIndex: number;
      client_name: string;
      date: string;
      amount: number;
    }>;
    totalAmount: number;
    totalRows: number;
  };
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export function ImportPreview({ preview, onConfirm, onCancel, loading }: ImportPreviewProps) {
  const newClients = preview.clientMatches.filter((m) => m.matchType === 'new');
  const fuzzyMatches = preview.clientMatches.filter((m) => m.matchType === 'fuzzy');

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-white rounded-lg border p-5">
        <h3 className="font-semibold text-lg mb-3">Import Summary</h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Total Rows</p>
            <p className="text-xl font-bold">{preview.totalRows}</p>
          </div>
          <div>
            <p className="text-gray-500">Total Amount</p>
            <p className="text-xl font-bold">{formatCurrency(preview.totalAmount)}</p>
          </div>
          <div>
            <p className="text-gray-500">Unique Clients</p>
            <p className="text-xl font-bold">{preview.clientMatches.length}</p>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {preview.duplicates.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-medium text-amber-800 mb-2">
            Potential Duplicates ({preview.duplicates.length})
          </h4>
          <ul className="text-sm text-amber-700 space-y-1">
            {preview.duplicates.map((d, i) => (
              <li key={i}>
                Row {d.rowIndex}: {d.client_name} - {formatDate(d.date)} - {formatCurrency(d.amount)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {newClients.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-2">
            New Clients ({newClients.length})
          </h4>
          <ul className="text-sm text-blue-700 space-y-1">
            {newClients.map((c, i) => (
              <li key={i}>{c.importName}</li>
            ))}
          </ul>
        </div>
      )}

      {fuzzyMatches.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-medium text-yellow-800 mb-2">
            Fuzzy Matches ({fuzzyMatches.length}) — Please Review
          </h4>
          <ul className="text-sm text-yellow-700 space-y-1">
            {fuzzyMatches.map((c, i) => (
              <li key={i}>
                "{c.importName}" matched to "{c.matchedClient?.name}"
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Transaction preview table */}
      <div className="bg-white rounded-lg border">
        <h4 className="font-medium p-4 border-b">Transaction Preview (first 20)</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-2 text-left text-xs text-gray-500">Date</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">Client</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">Type</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">Reference</th>
                <th className="px-4 py-2 text-right text-xs text-gray-500">Amount</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {preview.rows.slice(0, 20).map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{formatDate(r.date)}</td>
                  <td className="px-4 py-2">{r.client_name}</td>
                  <td className="px-4 py-2">{r.type}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.reference_number || '-'}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatCurrency(r.amount)}</td>
                  <td className="px-4 py-2 text-xs">{r.cert_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.rows.length > 20 && (
            <p className="text-xs text-gray-400 p-3 text-center">
              ...and {preview.rows.length - 20} more rows
            </p>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Importing...' : `Confirm Import (${preview.totalRows} rows)`}
        </button>
      </div>
    </div>
  );
}
